import {
  fiscalJobItemReason,
  FiscalJobItem,
  FiscalJobItemState,
  FiscalJobState
} from '../fiscal-jobs/fiscal-job.models';
import {
  addCalendarMonths,
  companyToday,
  DayKey,
  DEFAULT_COMPANY_TIME_ZONE,
  endOfMonth
} from '../../../../_shared/time/company-day';

/**
 * The DSFinV-K data export as the screen reads it (#770) — the wire types this surface adds on top of the
 * fiscal job engine's, plus the German words the screen puts on them.
 *
 * ### Nothing here names a provider
 *
 * That is the point of Feature #241, and it is what makes this file worth reading before changing it. No label,
 * no empty state and no error sentence says who performs the work; the export is described by **what it is**
 * (a DSFinV-K data export) and **what period it covers**. `DSFinV-K` is the Federal Ministry of Finance's own
 * name for the interface, so naming it is the opposite of naming a supplier.
 *
 * ### Why the German wording lives here and not in the templates
 *
 * Three surfaces render the same state — the run list, a run's entries and the alert after a refused create —
 * and a label written three times is a label that will eventually read three ways. The maps below are the one
 * place a state or an error code becomes a sentence, so they can also be asserted in one place.
 */

/** The period a run covers, as the two business days the server stored. */
export interface DataExportPeriod {
  /** The first business day, `yyyy-MM-dd`. */
  from: string;
  /** The last business day, inclusive, `yyyy-MM-dd`. */
  to: string;
  /**
   * The archive container the run was created for.
   *
   * Read back off the run rather than off the screen: a re-download next year is the same file in the same
   * wrapper, whatever the form happens to be showing.
   */
  format: FiscalArchiveFormat;
}

/** A register the export filter may cover. */
export interface FiscalExportRegister {
  /** The register's id — what a create request names. Never rendered. */
  id: string;
  /**
   * The operator's own name for it, or `null` when nobody has named it.
   *
   * **Nullable, and that is the fix** (#770, found on the live screen). It shipped as a plain string with an
   * id fallback on the server, and because a register's stored `Name` starts out holding its own id the screen
   * printed twelve GUIDs at an operator and their tax adviser. Carrying the ABSENCE instead lets each surface
   * do the right thing with it: the picker calls it {@link UNNAMED_REGISTER_LABEL}, and the "these registers
   * are not set up" sentence — which exists so a reader can identify them — counts them instead of listing
   * thirteen indistinguishable entries.
   */
  name: string | null;
  /**
   * Whether it has ever been set up for fiscal record keeping. A register that has not cannot appear in an
   * export, so the screen shows it and says so rather than offering a selection the server would refuse.
   */
  registered: boolean;
}

/**
 * What a register with no name of its own is called on screen.
 *
 * Mirrors `FiscalExportSelection.UnnamedRegisterLabel` server-side, which names the entries and the downloaded
 * file. It says what is TRUE rather than inventing a name, and an operator reading it knows what to do about
 * it.
 */
export const UNNAMED_REGISTER_LABEL = 'Kasse ohne Namen';

/** What to call a register in a picker: its own name, or {@link UNNAMED_REGISTER_LABEL}. Never its id. */
export function registerLabel(register: FiscalExportRegister): string {
  return register.name ?? UNNAMED_REGISTER_LABEL;
}

/**
 * The archive container a finished export is handed over in.
 *
 * Mirrors `FiscalArchiveFormat`/`FiscalArchive` server-side, including the wire spellings. **The far end
 * builds the export in whichever container was asked for** (#823) — nothing on this side repacks one
 * container into another; see `FiscalArchive.WireName` for where the choice travels into the create request.
 */
export type FiscalArchiveFormat = 'tar' | 'zip';

/**
 * The container a run's stored parameters mean when they say nothing at all.
 *
 * Only reached for a run created before the choice existed (#823) or one whose `format` this build cannot
 * read — every such run was built as a TAR, so this is the historically correct read-back, not a preference.
 * The screen's own default for a NEW export is {@link DEFAULT_UI_ARCHIVE_FORMAT}, a separate constant on
 * purpose: the two answer different questions and must be free to disagree.
 */
export const DEFAULT_ARCHIVE_FORMAT: FiscalArchiveFormat = 'tar';

/**
 * The container the create form preselects for a NEW export (#823, Niko's call).
 *
 * ZIP, because it is what opens without a conversion step in the validation tools an auditor actually runs —
 * the live check was closing #195's ZIP, taken straight from the fiskaly dashboard. TAR stays one click away
 * for whoever wants it, and an existing run's own container is never touched by this constant — see
 * {@link DEFAULT_ARCHIVE_FORMAT}.
 */
export const DEFAULT_UI_ARCHIVE_FORMAT: FiscalArchiveFormat = 'zip';

/** One option of the container picker. */
export interface ArchiveFormatOption {
  /** The wire spelling. */
  value: FiscalArchiveFormat;
  /** What the picker says on screen. */
  label: string;
}

/**
 * The containers the picker lists, the preselected one first.
 *
 * The labels name the FILE, not a tool: an operator recognises „ZIP" and „TAR" from what lands in their
 * downloads folder, never from who built it (#241).
 */
export const ARCHIVE_FORMAT_OPTIONS: readonly ArchiveFormatOption[] = [
  {value: 'zip', label: 'ZIP (empfohlen)'},
  {value: 'tar', label: 'TAR'}
];

/** What the run card and the create form call a container, for the format actually in play. */
export function archiveFormatLabel(format: FiscalArchiveFormat): string {
  return format === 'zip' ? 'ZIP' : 'TAR';
}

/** The file extension a container produces — used only for the fallback file name. */
export function archiveExtension(format: FiscalArchiveFormat): string {
  return format === 'zip' ? '.zip' : '.tar';
}

/** The media type a tar is served as. Mirrors `FiscalArchive.TarMediaType`. */
export const TAR_MEDIA_TYPE = 'application/x-tar';

/** The media type a zip is served as. Mirrors `FiscalArchive.ZipMediaType`. */
export const ZIP_MEDIA_TYPE = 'application/zip';

/**
 * The container a download's own `Content-Type` says it is.
 *
 * The server sets that header from `FiscalArchive.Detect`'s read of the BYTES, not from what the run asked
 * for (`FiscalExportService` builds the name and the media type from the same detected format), so it is the
 * one statement about the container that cannot disagree with the file. It is also CORS-safelisted, which
 * makes it readable in exactly the case the file name is not — a gateway that does not expose
 * `Content-Disposition`.
 *
 * @param contentType the response header, with or without parameters (`application/zip; charset=…`).
 * @returns the container, or {@link DEFAULT_ARCHIVE_FORMAT} when the header is absent or says something else.
 */
export function archiveFormatFromMediaType(contentType: string | null | undefined): FiscalArchiveFormat {
  const mediaType = (contentType ?? '').split(';')[0].trim().toLowerCase();
  if (mediaType === ZIP_MEDIA_TYPE) return 'zip';
  if (mediaType === TAR_MEDIA_TYPE) return 'tar';
  return DEFAULT_ARCHIVE_FORMAT;
}

/** What starting a data export asks for. */
export interface FiscalExportCreateRequest {
  /** The first business day, `yyyy-MM-dd`. */
  from: string;
  /** The last business day, inclusive, `yyyy-MM-dd`. */
  to: string;
  /** The registers to cover, or omitted/empty for every register of the location. */
  registerIds?: string[];
  /** The archive container, or omitted for {@link DEFAULT_ARCHIVE_FORMAT}. */
  format?: FiscalArchiveFormat;
}

/** A collected export: the bytes and the name the server chose for them. */
export interface FiscalExportFile {
  fileName: string;
  blob: Blob;
}

/**
 * The reference an entry carries when it covers every register of the location.
 *
 * Mirrors `FiscalExportSelection.AllRegistersReference` server-side. A sentinel rather than an empty string,
 * for the reason recorded there: a blank reference is indistinguishable from a missing one.
 */
export const ALL_REGISTERS_REFERENCE = '*';

/** Whether an entry covers every register rather than one. */
export function coversEveryRegister(reference: string | null | undefined): boolean {
  return !reference || reference === ALL_REGISTERS_REFERENCE;
}

/**
 * Reads a run's stored parameters back into the period it covers.
 *
 * **Tolerant on purpose.** The blob is written by one build and read by the next, and a run whose parameters a
 * later build cannot read has to degrade into "Zeitraum unbekannt" on a row rather than take the whole list
 * down with it.
 */
export function parseExportPeriod(parameters: string | null | undefined): DataExportPeriod | null {
  if (!parameters) return null;

  try {
    const parsed: unknown = JSON.parse(parameters);
    if (!parsed || typeof parsed !== 'object') return null;

    const {from, to, format} = parsed as Record<string, unknown>;
    if (typeof from !== 'string' || typeof to !== 'string') return null;
    if (!isIsoDay(from) || !isIsoDay(to)) return null;

    // Tolerant for the same reason the dates are parsed defensively: runs created before the container was
    // selectable carry no `format` at all, and they were all built as tar — so the default is also the
    // historically correct answer for them.
    return {from, to, format: format === 'zip' ? 'zip' : DEFAULT_ARCHIVE_FORMAT};
  } catch {
    return null;
  }
}

/** A period as it is written on screen: `01.01.2026 – 31.01.2026`. */
export function formatExportPeriod(period: DataExportPeriod | null): string {
  if (!period) return 'Zeitraum unbekannt';
  // An en dash with spaces, the German convention for a range — not a hyphen, which reads as part of a date
  // that is already full of hyphens.
  return `${formatDay(period.from)} – ${formatDay(period.to)}`;
}

/** One ISO day as `dd.MM.yyyy`, or the raw value when it is not one. */
export function formatDay(day: string): string {
  if (!isIsoDay(day)) return day;
  const [year, month, dayOfMonth] = day.split('-');
  return `${dayOfMonth}.${month}.${year}`;
}

/**
 * The two days the create form opens with.
 *
 * Deliberately **not** a {@link DataExportPeriod}: that shape describes a run that EXISTS, and a run's
 * container is a fact about it. Nothing has been created here yet, and the form's own container comes from
 * {@link DEFAULT_UI_ARCHIVE_FORMAT} — so carrying a `format` on this one meant a public shape holding a value
 * no caller reads and nobody would think to keep current.
 */
export interface DefaultExportPeriod {
  /** The first business day, `yyyy-MM-dd`. */
  from: DayKey;
  /** The last business day, inclusive, `yyyy-MM-dd`. */
  to: DayKey;
}

/**
 * The previous full calendar month, read on the COMPANY's calendar (#813).
 *
 * That is the period an operator asks for in practice: the current month is still being traded, so an export
 * of it is incomplete by definition.
 *
 * **Which month that is depends on the zone, and getting it from UTC was the bug.** Built from
 * `getUTCFullYear()`/`getUTCMonth()` — the same defect #813 removed from this file's `isoToday()` — the form
 * opened on 1.–30. November at 00:30 Berlin on 1 January, because UTC was still in December. A zone behind
 * UTC fails the same way at the other end of the month.
 *
 * @param timeZone the company's zone. Defaults to {@link DEFAULT_COMPANY_TIME_ZONE} because this runs at
 * field-initialiser time, before `CompanyTimeZoneService` has answered — which is exactly what that service's
 * own non-undefined fallback is for, so a caller holding it should pass `companyTimeZone.timeZone()`.
 * @param now the instant to read, injectable for tests.
 */
export function defaultPeriod(timeZone: string = DEFAULT_COMPANY_TIME_ZONE,
                              now: Date | number = Date.now()): DefaultExportPeriod {
  const firstOfLastMonth = addCalendarMonths(companyToday(timeZone, now), -1);
  return {from: firstOfLastMonth, to: endOfMonth(firstOfLastMonth)};
}

/** The German label for a run's state. */
export function exportRunStateLabel(state: FiscalJobState): string {
  return RUN_STATE_LABELS[state] ?? 'Unbekannt';
}

/** The German label for one entry's state. */
export function exportItemStateLabel(state: FiscalJobItemState): string {
  return ITEM_STATE_LABELS[state] ?? 'Unbekannt';
}

/** The chip colour an entry's state is drawn in. */
export type ExportTone = 'success' | 'running' | 'warning' | 'danger';

/**
 * The tone one entry's state is drawn in.
 *
 * The four unsuccessful states keep two different tones rather than collapsing into one red: `expired` and
 * `withdrawn` are "start it again", which is an instruction, while `failed` and `cancelled` are "something went
 * wrong". One badge for all four would tell three of those four people the same, mostly wrong, thing.
 */
export function exportItemTone(state: FiscalJobItemState): ExportTone {
  switch (state) {
    case 'completed':
      return 'success';
    case 'pending':
    case 'working':
      return 'running';
    case 'expired':
    case 'withdrawn':
      return 'warning';
    default:
      return 'danger';
  }
}

/**
 * The German sentence for an `E_FISCAL_*` code.
 *
 * The server's catalog descriptions are English — they are part of the API contract, branched on by their code
 * — so the screen owns the words. An unknown code falls back to a sentence that still tells the operator what
 * to do, because a code on screen is not an instruction.
 */
export function exportErrorMessage(code: string | null | undefined): string {
  return exportErrorSentence(code) ?? FALLBACK_ERROR;
}

/**
 * The German sentence for an `E_FISCAL_*` code, or `null` when this screen has none for it.
 *
 * Separate from {@link exportErrorMessage} so a caller can tell "we have no words for this" from "here are
 * our generic words". {@link exportItemReason} is the caller that needs the difference: it falls back to the
 * far end's own explanation before it falls back to a generic sentence, and a lookup that always answers
 * something makes that second chance unreachable.
 */
export function exportErrorSentence(code: string | null | undefined): string | null {
  if (!code) return null;
  return ERROR_MESSAGES[code] ?? null;
}

/**
 * Why one entry of an export did not come out, in German.
 *
 * Our own sentence for the code first, the server's sanitised `errorDetail` second, a generic sentence last —
 * the order and the reasoning live in {@link fiscalJobItemReason}, which this screen and the TSE export screen
 * both bind to so the two cannot drift apart.
 */
export function exportItemReason(item: FiscalJobItem): string {
  return fiscalJobItemReason(item, exportErrorSentence, FALLBACK_ERROR);
}

/** Whether a finished entry has a file to collect. */
export function isDownloadable(item: FiscalJobItem): boolean {
  return item.state === 'completed' && !!item.resultReference;
}

const RUN_STATE_LABELS: Record<FiscalJobState, string> = {
  queued: 'In der Warteschlange',
  running: 'Wird erstellt',
  completed: 'Fertig',
  completedWithErrors: 'Teilweise fertig',
  failed: 'Fehlgeschlagen'
};

const ITEM_STATE_LABELS: Record<FiscalJobItemState, string> = {
  pending: 'Wartet',
  working: 'Wird erstellt',
  completed: 'Fertig',
  failed: 'Fehlgeschlagen',
  cancelled: 'Abgebrochen',
  expired: 'Abgelaufen',
  withdrawn: 'Nicht mehr verfügbar'
};

const FALLBACK_ERROR =
  'Der Vorgang konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut oder wenden Sie sich an die ' +
  'Administration.';

/**
 * Every refusal this screen can meet, in German and in the formal Sie.
 *
 * Each sentence says what happened **and** what to do next. A message that only states a rule leaves the
 * reader to guess the remedy, and the remedy is the whole reason they are reading it.
 */
const ERROR_MESSAGES: Record<string, string> = {
  // ----- the request -----
  E_FISCAL_EXPORT_PERIOD_INVALID:
    'Der gewählte Zeitraum ist nicht gültig. Das Bis-Datum darf nicht vor dem Von-Datum liegen.',
  E_FISCAL_EXPORT_PERIOD_TOO_LONG:
    'Der gewählte Zeitraum umfasst mehr als zehn Jahre. Bitte grenzen Sie ihn ein.',
  E_FISCAL_EXPORT_REGISTER_UNKNOWN:
    'Eine der gewählten Kassen gehört nicht zu diesem Standort. Bitte laden Sie die Seite neu.',
  E_FISCAL_EXPORT_REGISTER_NOT_REGISTERED:
    'Diese Kasse ist noch nicht für die Kassenführung eingerichtet. Für sie liegen keine Daten zum Export vor.',
  // Deliberately says what is TRUE rather than pointing at the period: until #770's first live run this
  // arrived as "der Export wurde für den gewählten Zeitraum abgelehnt, bitte prüfen Sie den Zeitraum", which
  // blamed a period that was correct and left the reader with nothing to do.
  E_FISCAL_EXPORT_REGISTER_NO_RECORDS:
    'Für diese Kasse liegen im gewählten Zeitraum keine Kassenabschlüsse vor. Bitte wählen Sie einen anderen ' +
    'Zeitraum oder eine andere Kasse.',
  // The period is not fully transmitted, so an export of it would be SHORT — and a short DSFinV-K archive is
  // the worst shape a fiscal artefact can have: complete in appearance, incomplete in fact. Two codes rather
  // than one because the instruction differs; telling somebody to wait for something that has stopped is worse
  // than telling them nothing.
  E_FISCAL_EXPORT_PERIOD_TRANSMISSION_PENDING:
    'Für den gewählten Zeitraum sind noch nicht alle Kassenabschlüsse übermittelt. Der Export wäre ' +
    'unvollständig und wurde deshalb nicht erstellt. Die Übermittlung wird automatisch nachgeholt. Bitte ' +
    'versuchen Sie es in Kürze erneut.',
  E_FISCAL_EXPORT_PERIOD_TRANSMISSION_ABANDONED:
    'Für den gewählten Zeitraum konnten einzelne Kassenabschlüsse nicht übermittelt werden. Der Export wäre ' +
    'unvollständig und wurde deshalb nicht erstellt. Bitte wenden Sie sich an die Administration; dort ' +
    'lässt sich die Übermittlung nachholen.',
  // A closing-correction run (#771) is still withdrawing and re-filing one of the period's closings, so an
  // export right now would capture either the withdrawn document or none at all. Resolves itself once the
  // run finishes, unlike ABANDONED above — so the instruction is to wait, not to contact the Administration.
  E_FISCAL_EXPORT_CORRECTION_OUTSTANDING:
    'Für den gewählten Zeitraum werden noch Kassenabschlüsse erneut übermittelt. Der Export wäre ' +
    'unvollständig und wurde deshalb nicht erstellt. Bitte warten Sie, bis die erneute Übermittlung ' +
    'abgeschlossen ist, und versuchen Sie es dann erneut.',
  E_FISCAL_EXPORT_RECORDS_UNAVAILABLE:
    'Es konnte gerade nicht geprüft werden, welche Daten für diesen Zeitraum vorliegen. Es ist nichts ' +
    'verloren. Bitte versuchen Sie es in Kürze erneut.',
  E_FISCAL_EXPORT_FORMAT_UNKNOWN:
    'Dieses Archivformat ist nicht bekannt. Bitte laden Sie die Seite neu.',
  // Eigener Satz neben FORMAT_UNKNOWN, weil die Ursache eine andere ist: Das Format ist bekannt, wird aber
  // bewusst nicht erzeugt. Der Export geht so heraus, wie er entstanden ist.
  E_FISCAL_EXPORT_FORMAT_NOT_OFFERED:
    'Der Export wird so ausgeliefert, wie er erzeugt wurde. Ein anderes Archivformat lässt sich nicht ' +
    'anfordern.',
  E_FISCAL_EXPORT_ARCHIVE_UNREADABLE:
    'Der Export konnte nicht im gewählten Archivformat bereitgestellt werden. Er ist unverändert vorhanden ' +
    'und kann im Standardformat heruntergeladen werden.',
  // Deliberately says that nothing was handed over. The alternative — serving bytes nobody could identify
  // under the export's own name — produced a file an operator would have forwarded to a tax office (#770 D1).
  E_FISCAL_EXPORT_ARCHIVE_UNRECOGNISED:
    'Die abgerufene Datei ist kein lesbares Archiv und wurde deshalb nicht übergeben. Der Export selbst ist ' +
    'unverändert vorhanden. Bitte versuchen Sie es in Kürze erneut; hält es an, erstellen Sie den Export ' +
    'neu.',
  E_FISCAL_EXPORT_NOT_CONFIGURED:
    'Für diesen Standort ist keine Kassenführung eingerichtet. Es kann kein Datenexport erstellt werden.',
  // Der Satz darüber sagt etwas ÜBER den Standort aus, dieser nur, dass die Einstellung gerade nicht gelesen
  // werden konnte (#829). Beide über denselben Code auszugeben hieß, einem Standort, der täglich abschließt,
  // zu bescheinigen, er führe keine Kasse.
  E_FISCAL_EXPORT_SETTINGS_UNAVAILABLE:
    'Die Einstellungen zur Kassenführung konnten gerade nicht gelesen werden. Es ist nichts verloren. Bitte ' +
    'versuchen Sie es in Kürze erneut.',

  // ----- the download -----
  E_FISCAL_EXPORT_NOT_AN_EXPORT:
    'Dieser Vorgang ist kein Datenexport.',
  E_FISCAL_EXPORT_NOT_READY:
    'Dieser Export ist noch nicht fertig. Sobald er bereitsteht, können Sie ihn herunterladen.',
  E_FISCAL_EXPORT_DOWNLOAD_UNAVAILABLE:
    'Der Export konnte gerade nicht abgerufen werden. Er ist nicht verloren. Bitte versuchen Sie es in Kürze ' +
    'erneut.',
  // "Weg" und "gerade nicht erreichbar" sind gegensätzliche Anweisungen (#770 D4): bis dahin hat auch ein
  // gelöschter Export „bitte versuchen Sie es in Kürze erneut" gesagt – ein Rat, der nie eintreten konnte.
  E_FISCAL_EXPORT_DOWNLOAD_GONE:
    'Dieser Export ist am Ablageort nicht mehr vorhanden und kann nicht erneut heruntergeladen werden. ' +
    'Bitte erstellen Sie ihn für denselben Zeitraum neu.',
  E_FISCAL_EXPORT_DOWNLOAD_REFUSED:
    'Der Export konnte nicht abgerufen werden, weil der Zugriff auf den Datenspeicher abgelehnt wurde. Der ' +
    'Export ist unverändert vorhanden. Bitte wenden Sie sich an die Administration.',

  // ----- the engine -----
  E_FISCAL_JOB_TENANT_REQUIRED: 'Bitte wählen Sie zuerst einen Standort aus.',
  E_SESSION_SHARD_REQUIRED: 'Bitte wählen Sie zuerst einen Standort aus.',
  E_FISCAL_JOB_NO_ITEMS: 'Es wurde nichts ausgewählt, das exportiert werden könnte.',
  E_FISCAL_JOB_TOO_MANY_ITEMS:
    'Es wurden mehr Einträge ausgewählt, als ein Lauf umfassen darf. Bitte grenzen Sie die Auswahl ein.',
  E_FISCAL_JOB_NOT_FOUND: 'Der Export wurde nicht gefunden. Bitte laden Sie die Seite neu.',
  E_FISCAL_JOB_ITEM_NOT_FOUND: 'Der Eintrag wurde nicht gefunden. Bitte laden Sie die Seite neu.',
  E_FISCAL_JOB_ITEM_NOT_RETRYABLE:
    'Dieser Eintrag wird gerade noch verarbeitet. Bitte warten Sie, bis er abgeschlossen ist.',
  // Eigener Satz, weil die Abhilfe die umgekehrte ist: bei einem fertigen Eintrag hilft Warten nie, und ein
  // erneuter Lauf würde die bereits vorhandene Datei ersetzen (#770 D3).
  E_FISCAL_JOB_ITEM_ALREADY_COMPLETED:
    'Dieser Eintrag ist bereits fertig. Die erstellte Datei steht weiterhin zur Verfügung; ein erneuter Lauf ' +
    'würde sie ersetzen.',
  E_FISCAL_JOB_NO_HANDLER:
    'Diese Art von Export steht auf dieser Installation nicht zur Verfügung. Bitte wenden Sie sich an die ' +
    'Administration.',
  E_FISCAL_JOB_OPERATION_FAILED:
    'Dieser Eintrag konnte nicht erstellt werden. Sie können ihn einzeln erneut versuchen.',
  E_FISCAL_JOB_OPERATION_CANCELLED: 'Die Erstellung dieses Eintrags wurde vorzeitig beendet.',
  E_FISCAL_JOB_OPERATION_EXPIRED:
    'Dieser Eintrag wurde nicht rechtzeitig fertig und muss neu angefordert werden.',
  E_FISCAL_JOB_OPERATION_WITHDRAWN:
    'Das Ergebnis dieses Eintrags ist nicht mehr verfügbar und muss neu erstellt werden.',
  E_FISCAL_JOB_POLL_BUDGET_EXHAUSTED:
    'Dieser Eintrag hat länger gedauert als erwartet und wird nicht mehr beobachtet. Sie können ihn einzeln ' +
    'erneut versuchen.',
  E_FISCAL_JOB_START_INTERRUPTED:
    'Der Start dieses Eintrags wurde unterbrochen. Bitte prüfen Sie ihn, bevor Sie ihn erneut versuchen.',
  E_FISCAL_JOB_INFRASTRUCTURE_UNAVAILABLE:
    'Ein benötigtes System war nicht erreichbar. Sie können diesen Eintrag erneut versuchen.'
};

function isIsoDay(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}
